import { ESQuestion } from '~src/interfaces/question.interface';
import { Scale } from '~src/interfaces/scale.interface';
import { Segment, UserSegment } from '~src/interfaces/segment.interface';
import { SurveyStatus, SurveySubjectTypes } from '~src/interfaces/surveys.interfaces';
import { TopicSubject } from '~src/interfaces/topic.interface';
import { SurveyScopes } from '~src/modules/surveys/interfaces';
import { SvgIconName } from '~src/shared/SvgIcon';

import { AvatarType } from '~src/shared/avatarBox/AvatarBox';
import { QuestionSummary } from './questions.interface';
import { ManagerAttrs } from '~src/interfaces/user.interface';
import {ContentLocalesI} from "~src/interfaces/session.types";

export enum ReportEntityTypes {
  Company = 'company',
  User = 'user',
  Group = 'group',
  Survey = 'survey',
  Total = 'total',
  FeedbackMeeting = 'FeedbackMeeting',
}

export const toAvatarType = (eType: ReportEntityTypes): AvatarType => {
  switch (eType) {
    case ReportEntityTypes.Company:
      return AvatarType.Company;
    case ReportEntityTypes.User:
      return AvatarType.User;
    case ReportEntityTypes.Group:
      return AvatarType.Group;
    case ReportEntityTypes.Total:
      return AvatarType.Total;
    case ReportEntityTypes.FeedbackMeeting:
      return AvatarType.User;
    default:
      return AvatarType.Total;
  }
};

export type ProfileTypes = 'companyProfile' | 'userProfile' | 'groupProfile' | 'surveyProfile';

export enum ReportSubjectTypes {
  Direct = 'direct',
  Question = 'question',
  Topic = 'topic',
  Answer = 'answer',
  News = 'news',
  Participant = 'participant',
  QuestionsImprove = 'questions_improve',
  TopicsImprove = 'topics_improve',
  Improve = 'improve',
  ManagerTeam = 'manager_team',
  UserRequests = 'user_requests',
}

export enum ReportsLevels {
  Topic = 'topic',
  Question = 'question',
  Answer = 'answer',
}

export const reportLevels = [ReportsLevels.Topic, ReportsLevels.Question, ReportsLevels.Answer];

export enum ReportsIconType {
  Company = 'company',
  User = 'user',
  Group = 'group',
  Survey = 'survey',
  Topic = 'topic',
  Question = 'question',
}

export interface ReportsObj {
  name: string;
  id: string;
  type?: SurveySubjectTypes | TopicSubject;
  surveyStatus?: SurveyStatus;
  surveyScope?: SurveyScopes;
  withLink?: boolean;
  description?: ContentLocalesI | null;
  imageUrl?: string;
  manager_attributes?: ManagerAttrs;
}

export interface UserReportsObj extends ReportsObj {
  manager_attributes: ManagerAttrs;
}

export interface HeaderInformation {
  selectedTabIndex: number;
  tabs: Tab[];
  subject: ReportsObj | null;
  entity: ReportsObj | null;
  entityType: ReportEntityTypes;
  subjectType: ReportSubjectTypes;
  breadCrumbLinks: BreadCrumb[];
  depth: number;
  iconName?: SvgIconName;
  feedbackPeriods?: Array<{ value: string; name: string }>;
  selectedFeedbackPeriod?: string;
}

export interface SummaryNumbers {
  avg_rating_ord: number;
  number_of_comments: number;
  number_of_answers: number;
  top_chosen_option: number;
}

export interface GapAnalysisI {
  self: SummaryNumbers;
  peer: SummaryNumbers;
  manager: SummaryNumbers | null;
  team: SummaryNumbers;
}

export interface Tab {
  name: string;
  translationKey: string;
  translationParams?: Record<string, string>;
  link: string;
  hasAbsoluteLink?: boolean;
}

export interface BreadCrumb {
  name: string;
  link?: string;
}

export interface HistogramItem {
  avg_rating_ord: number;
  date: string;
  number_of_comments: number;
  number_of_answers: number;
}

export interface SummaryResult extends SummaryNumbers {
  number_of_users?: number;
  gap_analysis?: GapAnalysisI | null;
}

export interface HeaderRating {
  result: SummaryResult;
  scale: Scale;
  ratingIndex?: number;
  manager?: UserSegment | null;
}

export interface ResponseOptions {
  auth_tenant_id: string | null;
  auth_user_id: string | null;
  group_id: string | null;
  question_id: string | null;
  topic_id: string | null;
  url_values: any;
  user_id: string | null;
}

export interface ReportsTopic {
  created_at: string;
  description: { [key: string]: string };
  id: string;
  name: { [key: string]: string };
  scale_id: string;
  scale_version_id: string;
  type: TopicSubject;
  updated_at: string;
}

export interface AnswerSummary {
  question: ESQuestion;
  topic: ReportsTopic;
  options?: ResponseOptions;
  histogram: HistogramItem[];
  result: SummaryResult;
  manager: Segment;
}

export type SummaryType = QuestionSummary | AnswerSummary;

export interface HeaderSummaries {
  loading: boolean;
  question: QuestionSummary | null;
  answer: AnswerSummary | null;
}

export enum SectionType {
  SelfAssessment = 'self_assessment',
  Manager = 'manager',
  Peer = 'peer',
  Team = 'team',
}

export enum ReportSubjectTabs {
  News = 'NEWS',
  Topics = 'TOPICS',
  Participants = 'PARTICIPANTS',
  Direct = 'DIRECT',
  MyRequests = 'MY_REQUESTS',
  MyTeam = 'MY_TEAM',
  Notes = 'NOTES',
  Meetings = 'MEETINGS',
  Questions = 'QUESTIONS',
  Improve = 'IMPROVE',
  Answers = 'ANSWERS',
  GapAnalysis = 'GAP ANALYSIS',
}
